import React from "react"
import { Link } from "react-router-dom"
import { Tab, TabClasses, Tabs, TabsClasses, Tooltip } from "@mui/material"
import {
  NavPillsMode,
  NavPillsOrientation,
  NavPillsSize,
  NavPillsType,
  NavPillsVariant
} from "./types"
import { useStyles } from "./styles"

export type Props = {
  id?: string
  value?: string
  items: NavPillsType[]
  size?: NavPillsSize
  mode?: NavPillsMode
  variant?: NavPillsVariant
  orientation?: NavPillsOrientation
  disableIndicator?: boolean
  tabClasses?: Partial<TabClasses>
  tabsClasses?: Partial<TabsClasses>
  dataAutomationId?: string
  onTabChange?: (value: string) => void
  component?: React.ElementType
}

const NavPills: React.FC<Props> = ({
  id,
  value,
  items,
  size = "md",
  mode = "light",
  variant = "text",
  orientation = "horizontal",
  disableIndicator = false,
  tabClasses,
  tabsClasses,
  dataAutomationId,
  onTabChange,
  component = "nav"
}) => {
  const { classes, cx } = useStyles({ size, mode, variant })

  return (
    <Tabs
      scrollButtons="auto"
      allowScrollButtonsMobile
      variant="scrollable"
      textColor="primary"
      indicatorColor="primary"
      component={component}
      orientation={orientation}
      value={value || false}
      classes={{
        ...(tabsClasses || {}),
        indicator: cx(classes.tabIndicator, tabsClasses?.indicator, {
          [classes.disableTabIndicator]: disableIndicator
        }),
        root: cx(classes.tabsRoot, tabsClasses?.root),
        scroller: cx(classes.tabScroller, tabsClasses?.scroller),
        flexContainer: cx(classes.tabsFlexContainer, tabsClasses?.flexContainer)
      }}
      data-automation-id={dataAutomationId ?? "subpage-tabs-container"}
    >
      {items.map(tab => (
        <Tab
          disableRipple
          id={id || "FIRST_NAV_PILLS_TAB"}
          disabled={!!tab.disabled}
          key={tab.value}
          tabIndex={0}
          classes={{
            ...(tabClasses || {}),
            root: cx(classes.tabRoot, tabClasses?.root)
          }}
          value={tab.value}
          label={
            tab.tooltipText ? (
              <Tooltip arrow title={tab.tooltipText}>
                <span>{tab.label}</span>
              </Tooltip>
            ) : (
              tab.label
            )
          }
          onClick={() => onTabChange?.(tab.value)}
          component={tab.href ? Link : undefined}
          to={tab.href}
          icon={tab.icon}
          iconPosition={tab.iconPosition}
          data-automation-id={tab.dataAutomationId || `${tab.value}-tab`}
        />
      ))}
    </Tabs>
  )
}

export default NavPills
